import {
  Question,
  Answers,
  Stage,
  RiskLevel,
  IdentifiedRisk,
  RecommendedPath,
  AssessmentResult
} from "./types";

// ── Questions ──────────────────────────────────────────────────────────────

export const questions: Question[] = [
  // BLOCK A — PROJECT STATUS
  {
    id: "a1",
    block: "A",
    blockLabel: "Project Status",
    question: "Is your application currently live in production?",
    options: [
      { label: "No, it is still a prototype", value: "prototype" },
      {
        label: "Yes, it is live with internal users only",
        value: "internal"
      },
      {
        label: "Yes, it is live with external users",
        value: "external"
      },
      { label: "Yes, it has paying customers", value: "paying" }
    ]
  },
  {
    id: "a2",
    block: "A",
    blockLabel: "Project Status",
    question:
      "Approximately how many active users does your application currently have?",
    options: [
      { label: "Fewer than 50", value: "lt50" },
      { label: "50 - 500", value: "50-500" },
      { label: "500 - 5,000", value: "500-5000" },
      { label: "More than 5,000", value: "gt5000" }
    ]
  },
  {
    id: "a3",
    block: "A",
    blockLabel: "Project Status",
    question:
      "When do you plan to scale or present this product externally (investors, enterprise clients, marketing push)?",
    options: [
      { label: "No clear timeline", value: "none" },
      { label: "Within the next 3 months", value: "3months" },
      { label: "Within the next 30 days", value: "30days" },
      { label: "It is already under real load", value: "live" }
    ]
  },

  // BLOCK B — DEPLOYMENT & INFRASTRUCTURE
  {
    id: "b1",
    block: "B",
    blockLabel: "Deployment & Infrastructure",
    question:
      "Do you have a dedicated staging environment separate from production?",
    options: [
      { label: "Yes", value: "yes" },
      { label: "No", value: "no" },
      { label: "Not sure", value: "unsure" }
    ]
  },
  {
    id: "b2",
    block: "B",
    blockLabel: "Deployment & Infrastructure",
    question: "How are deployments handled?",
    options: [
      {
        label: "Manual push directly to production",
        value: "manual"
      },
      {
        label: "Basic CI/CD without automated checks",
        value: "basic-cicd"
      },
      {
        label: "Structured CI/CD with testing and review",
        value: "structured-cicd"
      },
      { label: "Managed by an external agency", value: "agency" }
    ]
  },
  {
    id: "b3",
    block: "B",
    blockLabel: "Deployment & Infrastructure",
    question:
      "If production breaks, can you roll back safely within 10 minutes?",
    options: [
      { label: "Yes", value: "yes" },
      { label: "No", value: "no" },
      { label: "Not sure", value: "unsure" }
    ]
  },

  // BLOCK C — SECURITY & STABILITY
  {
    id: "c1",
    block: "C",
    blockLabel: "Security & Stability",
    question:
      "Do you have environment separation (development / staging / production)?",
    options: [
      { label: "Yes", value: "yes" },
      { label: "No", value: "no" },
      { label: "Not sure", value: "unsure" }
    ]
  },
  {
    id: "c2",
    block: "C",
    blockLabel: "Security & Stability",
    question:
      "Do you use production monitoring (for example error tracking or performance monitoring tools)?",
    options: [
      { label: "Yes", value: "yes" },
      { label: "No", value: "no" }
    ]
  },
  {
    id: "c3",
    block: "C",
    blockLabel: "Security & Stability",
    question:
      "Have authentication, authorization, and security headers been reviewed by an experienced engineer?",
    options: [
      { label: "Yes", value: "yes" },
      { label: "No", value: "no" },
      { label: "Not sure", value: "unsure" }
    ]
  },

  // BLOCK D — TESTING & CODE QUALITY
  {
    id: "d1",
    block: "D",
    blockLabel: "Testing & Code Quality",
    question: "Do you have automated tests in place?",
    options: [
      { label: "No tests", value: "none" },
      { label: "Basic unit tests", value: "basic" },
      { label: "Tests for critical user flows", value: "critical" },
      {
        label: "Structured test coverage across the app",
        value: "structured"
      }
    ]
  },
  {
    id: "d2",
    block: "D",
    blockLabel: "Testing & Code Quality",
    question:
      "Who currently understands the application architecture at a deep level?",
    options: [
      {
        label: "I built it with AI and mostly understand it",
        value: "ai-built"
      },
      { label: "An internal developer or team", value: "internal" },
      { label: "A previous agency or freelancer", value: "agency" },
      { label: "No one fully understands it", value: "nobody" }
    ]
  },
  {
    id: "d3",
    block: "D",
    blockLabel: "Testing & Code Quality",
    question:
      "Who is responsible for long-term technical decisions and architecture?",
    options: [
      { label: "No one clearly owns it", value: "nobody" },
      { label: "Internal CTO / Head of Engineering", value: "cto" },
      { label: "External technical partner", value: "external" },
      { label: "Responsibility is unclear", value: "unclear" }
    ]
  },

  // BLOCK E — BUSINESS & QUALIFICATION
  {
    id: "e1",
    block: "E",
    blockLabel: "Business & Qualification",
    question: "How many full-time employees does your company have?",
    options: [
      { label: "1 (solo founder)", value: "1" },
      { label: "2 - 10", value: "2-10" },
      { label: "10 - 20", value: "10-20" },
      { label: "20 - 50", value: "20-50" },
      { label: "50+", value: "50+" }
    ]
  },
  {
    id: "e2",
    block: "E",
    blockLabel: "Business & Qualification",
    question:
      "What best describes your company's current financial stage?",
    options: [
      { label: "No revenue yet", value: "no-revenue" },
      { label: "Early revenue", value: "early-revenue" },
      { label: "Seed or Series A funded", value: "funded" },
      {
        label: "Established revenue-generating company",
        value: "established"
      }
    ]
  },
  {
    id: "e3",
    block: "E",
    blockLabel: "Business & Qualification",
    question: "Who is completing this assessment?",
    options: [
      { label: "Founder / CEO", value: "founder" },
      { label: "CTO / Head of Engineering", value: "cto" },
      { label: "Product or Marketing Lead", value: "product" },
      { label: "Developer", value: "developer" }
    ]
  }
];

// ── Helpers ────────────────────────────────────────────────────────────────

export function getBlocks(): { key: string; label: string }[] {
  const seen = new Set<string>();

  return questions.reduce<{ key: string; label: string }[]>(
    (acc, q) => {
      if (!seen.has(q.block)) {
        seen.add(q.block);
        acc.push({ key: q.block, label: q.blockLabel });
      }

      return acc;
    },
    []
  );
}

// ── Classification Logic ───────────────────────────────────────────────────

function isPreICP(answers: Answers): boolean {
  return (
    answers.e2 === "no-revenue" &&
    answers.a3 === "none" &&
    answers.e1 === "1" &&
    answers.a1 === "prototype"
  );
}

export function classifyStage(answers: Answers): Stage {
  if (isPreICP(answers)) return "pre-icp";

  const status = answers.a1;
  const users = answers.a2;
  const deployment = answers.b2;
  const monitoring = answers.c2;
  const testing = answers.d1;
  const rollback = answers.b3;
  const ownership = answers.d3;

  const isLive = status === "external" || status === "paying";
  const isLiveInternal = status === "internal";
  const hasRealUsers = isLive;
  const fewUsers = users === "lt50";
  const noOrManualDeploy =
    deployment === "manual" || deployment === "agency";
  const noMonitoring = monitoring === "no";
  const noTesting = testing === "none";
  const unsafeRollback = rollback === "no" || rollback === "unsure";
  const hasCICD =
    deployment === "basic-cicd" || deployment === "structured-cicd";
  const hasMonitoring = monitoring === "yes";
  const hasSomeTesting =
    testing === "basic" ||
    testing === "critical" ||
    testing === "structured";
  const weakOwnership =
    ownership === "nobody" ||
    ownership === "unclear" ||
    ownership === "external";

  // Stage 1 — AI Prototype
  if ((!isLive && !isLiveInternal) || (isLiveInternal && fewUsers)) {
    if (noOrManualDeploy && noMonitoring && noTesting) {
      return "stage-1";
    }
  }

  // Stage 3 — Stabilized Application (check before Stage 2)
  if (hasRealUsers && hasCICD && hasMonitoring && hasSomeTesting) {
    return "stage-3";
  }

  // Stage 2 — Broken Production
  if (hasRealUsers || isLiveInternal) {
    if (
      noOrManualDeploy ||
      unsafeRollback ||
      noMonitoring ||
      noTesting
    ) {
      if (weakOwnership || noOrManualDeploy || noMonitoring) {
        return "stage-2";
      }
    }
  }

  // Fallback
  if (hasRealUsers || isLiveInternal) return "stage-2";

  return "stage-1";
}

export function identifyRisks(
  answers: Answers,
  stage: Stage
): IdentifiedRisk[] {
  const risks: IdentifiedRisk[] = [];

  const deployment = answers.b2;
  const rollback = answers.b3;
  const staging = answers.b1;
  const monitoring = answers.c2;
  const security = answers.c3;
  const envSep = answers.c1;
  const testing = answers.d1;
  const ownership = answers.d3;
  const understanding = answers.d2;
  const users = answers.a2;
  const timeline = answers.a3;

  // Deployment Risk
  if (deployment === "manual" || deployment === "agency") {
    risks.push({
      category: "Deployment Risk",
      severity: "high",
      explanation:
        "Manual production deployments without reliable rollback increase the probability of downtime."
    });
  } else if (
    deployment === "basic-cicd" &&
    (rollback === "no" || rollback === "unsure")
  ) {
    risks.push({
      category: "Deployment Risk",
      severity: "medium",
      explanation:
        "CI/CD is in place but without confirmed rollback capability, recovery from failed deployments is uncertain."
    });
  }

  // Security Risk
  if (security === "no" || security === "unsure") {
    risks.push({
      category: "Security Risk",
      severity: security === "no" ? "high" : "medium",
      explanation:
        "No confirmed structured security review of authentication and environment separation."
    });
  }

  // Stability Risk
  if (monitoring === "no") {
    risks.push({
      category: "Stability Risk",
      severity: "high",
      explanation:
        "No production monitoring means errors and performance degradation go undetected until users report them."
    });
  }

  if (envSep === "no" || envSep === "unsure") {
    if (!risks.find((r) => r.category === "Stability Risk")) {
      risks.push({
        category: "Stability Risk",
        severity: "medium",
        explanation:
          "No confirmed environment separation increases the risk of untested changes reaching production."
      });
    }
  }

  // Scalability Risk
  if (testing === "none") {
    risks.push({
      category: "Scalability Risk",
      severity: "high",
      explanation:
        "No automated tests. Code changes cannot be safely validated, making scaling dangerous."
    });
  } else if (testing === "basic") {
    if (users === "500-5000" || users === "gt5000") {
      risks.push({
        category: "Scalability Risk",
        severity: "medium",
        explanation:
          "Only basic unit tests with significant user base. Critical user flows are not covered by automated tests."
      });
    }
  }

  // Ownership Risk
  if (
    ownership === "nobody" ||
    ownership === "unclear" ||
    understanding === "nobody" ||
    understanding === "ai-built"
  ) {
    risks.push({
      category: "Ownership Risk",
      severity:
        ownership === "nobody" || understanding === "nobody"
          ? "high"
          : "medium",
      explanation:
        "No clearly defined long-term technical owner. Knowledge concentration creates a single point of failure."
    });
  }

  // Pre-ICP specific risks
  if (stage === "pre-icp") {
    risks.push({
      category: "Scalability Risk",
      severity: "high",
      explanation:
        "Architecture not validated under load. No structured deployment or monitoring in place."
    });

    if (risks.length < 2) {
      risks.push({
        category: "Stability Risk",
        severity: "high",
        explanation:
          "No structured deployment or monitoring infrastructure for production use."
      });
    }
  }

  // Ensure minimum 2 risks
  if (risks.length < 2) {
    if (
      !risks.find((r) => r.category === "Business Risk") &&
      (timeline === "30days" || timeline === "live")
    ) {
      risks.push({
        category: "Business Risk",
        severity: "medium",
        explanation:
          "Aggressive timeline with unresolved technical gaps may create compounding issues under pressure."
      });
    }

    if (risks.length < 2 && staging === "no") {
      risks.push({
        category: "Deployment Risk",
        severity: "medium",
        explanation:
          "No staging environment. Changes are tested directly in production."
      });
    }
  }

  // Cap at 5 risks
  return risks.slice(0, 5);
}

export function computeRiskLevel(risks: IdentifiedRisk[]): RiskLevel {
  const highCount = risks.filter((r) => r.severity === "high").length;
  const mediumCount = risks.filter(
    (r) => r.severity === "medium"
  ).length;

  if (highCount >= 2) return "high";
  if (highCount === 1 && mediumCount >= 1) return "high";
  if (mediumCount >= 2) return "medium";
  if (highCount === 1 || mediumCount >= 1) return "medium";

  return "low";
}

function getStageName(stage: Stage): string {
  switch (stage) {
    case "pre-icp":
      return "Early / Experimental";
    case "stage-1":
      return "AI Prototype";
    case "stage-2":
      return "Broken Production";
    case "stage-3":
      return "Stabilized Application";
    default:
      return stage;
  }
}

function getRiskSummary(riskLevel: RiskLevel, stage: Stage): string {
  if (riskLevel === "high") {
    if (stage === "stage-2")
      return "Scaling at this stage may amplify instability.";

    return "Multiple high-severity risks require immediate attention before scaling.";
  }

  if (riskLevel === "medium") {
    return "Addressable gaps exist that should be resolved before significant growth.";
  }

  return "Core fundamentals are in place. Focus on long-term sustainability.";
}

function getRecommendedPath(stage: Stage): RecommendedPath {
  switch (stage) {
    case "pre-icp":
      return {
        name: "Production Readiness Checklist",
        description:
          "This checklist will help you validate core production fundamentals before engaging in structured technical work.",
        cta: "Download Checklist",
        ctaUrl: "#checklist",
        secondaryCta: "Talk to Our Team",
        secondaryCtaUrl: "#contact"
      };
    case "stage-1":
      return {
        name: "Vibe to Live",
        description:
          "We review your AI-built prototype, fix critical security and infrastructure gaps, and prepare your app for real users within a structured sprint.",
        cta: "Book Vibe Check",
        ctaUrl: "#vibe-check",
        secondaryCta: "Talk to Our Team",
        secondaryCtaUrl: "#contact"
      };
    case "stage-2":
      return {
        name: "Next.js Rescue",
        description:
          "Within 1\u20132 weeks we regain deployment control, eliminate critical production risks, and stabilize performance before transitioning into structured technical ownership.",
        cta: "Apply for Rescue Review",
        ctaUrl: "#rescue",
        secondaryCta: "Talk to Our Team",
        secondaryCtaUrl: "#contact"
      };
    case "stage-3":
      return {
        name: "SLA \u2014 Long-term Technical Ownership",
        description:
          "Monthly capacity, proactive maintenance, and a clear responsibility model to keep your application reliable as you grow.",
        cta: "Schedule Technical Acceptance Call",
        ctaUrl: "#sla",
        secondaryCta: "Talk to Our Team",
        secondaryCtaUrl: "#contact"
      };
    default:
      return {
        name: "Talk to Our Team",
        description: "Let us help you find the right path forward.",
        cta: "Get in Touch",
        ctaUrl: "#contact"
      };
  }
}

function generateExplanation(stage: Stage, answers: Answers): string {
  const monitoring = answers.c2;
  const testing = answers.d1;
  const deployment = answers.b2;
  const rollback = answers.b3;

  switch (stage) {
    case "pre-icp":
      return "Your project is in the early exploratory phase without revenue, a launch timeline, or user traction. This is the right time to validate your approach with a structured checklist before investing in production infrastructure.";

    case "stage-1": {
      const gaps: string[] = [];

      if (deployment === "manual") gaps.push("manual deployments");
      if (monitoring === "no") gaps.push("no monitoring");
      if (testing === "none") gaps.push("no automated tests");

      return `Your application is still in prototype stage${
        gaps.length > 0 ? ` with ${gaps.join(", ")}` : ""
      }. It needs production hardening before real users interact with it. Security, deployment safety, and basic observability must be established.`;
    }

    case "stage-2": {
      const riskItems: string[] = [];

      if (deployment === "manual" || deployment === "agency")
        riskItems.push("unsafe deployment process");
      if (rollback === "no" || rollback === "unsure")
        riskItems.push("no rollback capability");
      if (monitoring === "no") riskItems.push("missing monitoring");
      if (testing === "none") riskItems.push("no automated testing");

      return `Your application is live with real users but ${
        riskItems.length > 0
          ? `has critical gaps: ${riskItems.join(", ")}`
          : "has infrastructure gaps"
      }. This creates scaling and stability risk that needs immediate attention.`;
    }

    case "stage-3":
      return "Your application is live with CI/CD, monitoring, and some testing in place. The foundation is solid, but long-term ownership, proactive maintenance, and continuous improvement are needed to sustain reliability as you scale.";
    default:
      return "Your project needs further assessment to determine the best path forward.";
  }
}

export function evaluateAssessment(
  answers: Answers
): AssessmentResult {
  const stage = classifyStage(answers);
  const risks = identifyRisks(answers, stage);
  const riskLevel = computeRiskLevel(risks);
  const riskSummary = getRiskSummary(riskLevel, stage);
  const recommendedPath = getRecommendedPath(stage);
  const explanation = generateExplanation(stage, answers);

  return {
    stage,
    stageLabel: getStageName(stage),
    riskLevel,
    explanation,
    risks,
    riskSummary,
    recommendedPath
  };
}
